<script lang="ts" setup>
import { inject, ref } from "vue";
import { TranslationService } from "../../services/translation.service";
import { vDropdown as dropdownDirective } from "../../directives/v-dropdown";

const vDropdown = dropdownDirective;
interface IUnit {
    unit: string;
    faktor: number;
}

interface IModel {
    sdUnit: IUnit;
    smmUnit: IUnit;
    mcUnit: IUnit;
    mcValue: number | undefined;
    smmValue: number | undefined;
    sdValue: number | undefined;
    ppmValue: number | undefined;
    mplValue: number | undefined;
}

const t = (inject<TranslationService>("translationSvc") as TranslationService)
    .t;

const unitCategories = {
    soluteDensity: [
        { unit: "kg/m³", faktor: 1 },
        { unit: "kg/dm³", faktor: 0.001 },
        { unit: "kg/L", faktor: 0.001 },
        { unit: "g/mL", faktor: 0.001 },
        { unit: "t/m³", faktor: 0.001 },
        { unit: "g/cm³", faktor: 0.001 },
        { unit: "oz/cu in", faktor: 0.000578 },
        { unit: "lb/cu in", faktor: 0.00003613 },
        { unit: "lb/cu ft", faktor: 0.06243 },
        { unit: "lb/cu yd", faktor: 1.6856 },
        { unit: "lb/US gal", faktor: 0.008345 },
        { unit: "g/L", faktor: 1 },
        { unit: "g/dL", faktor: 0.1 },
        { unit: "g/dL", faktor: 1000 },
    ],
    soluteMolarMass: [
        { unit: "g/mol", faktor: 1 },
        { unit: "µg/mol", faktor: 1000000 },
        { unit: "mg/mol", faktor: 1000 },
        { unit: "dag/mol", faktor: 0.1 },
        { unit: "kg/mol", faktor: 0.001 },
        { unit: "t/mol", faktor: 0.000001 },
        { unit: "gr/mol", faktor: 15.432 },
        { unit: "dr/mol", faktor: 0.5644 },
        { unit: "oz/mol", faktor: 0.035274 },
        { unit: "lb/mol", faktor: 0.0022046 },
        { unit: "stone/mol", faktor: 0.00015747 },
        { unit: "Us ton/mol", faktor: 0.0000011023 },
        { unit: "Long ton/mol", faktor: 0.0000009842 },
        { unit: "earths/mol", faktor: 1.6744248350691537456883560496969e-28 },
        { unit: "me/mol", faktor: 1.097769122809900022019746757e27 },
        { unit: "u/mol", faktor: 6.02214e23 },
        { unit: "oz t/mol", faktor: 0.03215 },
    ],
    molarConcentration: [
        { unit: "M", faktor: 1 },
        { unit: "mM", faktor: 1000 },
        { unit: "μM", faktor: 1000000 },
        { unit: "nM", faktor: 1000000000 },
        { unit: "pM", faktor: 1000000000000 },
        { unit: "fM", faktor: 1000000000000000 },
        { unit: "aM", faktor: 1e18 },
        { unit: "fM", faktor: 1e21 },
        { unit: "fM", faktor: 1e24 },
    ],
};

const model = ref<IModel>({
    sdUnit: unitCategories.soluteDensity[0],
    smmUnit: unitCategories.soluteMolarMass[0],
    mcUnit: unitCategories.molarConcentration[0],
    mcValue: undefined,
    smmValue: undefined,
    sdValue: undefined,
    ppmValue: undefined,
    mplValue: undefined,
});

const onSelectSdUnit = (unit: IUnit) => {
    const prevUnit = model.value.sdUnit;
    model.value.sdUnit = unit;

    if (model.value.sdValue) {
        model.value.sdValue = _convertUnit(model.value.sdValue, prevUnit, unit);
    }
};

const onSelectSmmUnit = (unit: IUnit) => {
    const prevUnit = model.value.smmUnit;
    model.value.smmUnit = unit;

    if (model.value.smmValue) {
        model.value.smmValue = _convertUnit(
            model.value.smmValue,
            prevUnit,
            unit,
        );
    }
};

const onSelectMcUnit = (unit: IUnit) => {
    const prevUnit = model.value.mcUnit;
    model.value.mcUnit = unit;

    if (model.value.mcValue) {
        model.value.mcValue = _convertUnit(model.value.mcValue, prevUnit, unit);
    }
};

const _convertUnit = (val: number, prevUnit: IUnit, unit: IUnit) => {
    const initValue = val / prevUnit.faktor;
    return cleanNr(initValue * unit.faktor);
};

const calculate = (changedInput: string) => {
    const sdUnit = model.value.sdUnit;
    const sdValue = model.value.sdValue
        ? Number(model.value.sdValue)
        : undefined;
    const ppmValue = model.value.ppmValue
        ? Number(model.value.ppmValue)
        : undefined;
    const mplValue = model.value.mplValue
        ? Number(model.value.mplValue)
        : undefined;

    if (ppmValue && sdValue && mplValue) {
        switch (changedInput) {
            case "ppm":
            case "sd":
                model.value.mplValue = _calcMpl(sdValue, sdUnit, ppmValue);
                break;
            case "mpl":
                model.value.ppmValue = _calcPpm(mplValue, sdValue, sdUnit);
            default:
                break;
        }
        return;
    }

    if (ppmValue && sdValue) {
        model.value.mplValue = _calcMpl(sdValue, sdUnit, ppmValue);
        return;
    }

    if (mplValue && ppmValue) {
        model.value.sdValue = _calcSd(mplValue, ppmValue, sdUnit);
        return;
    }

    if (mplValue && sdValue) {
        model.value.ppmValue = _calcPpm(mplValue, sdValue, sdUnit);
        return;
    }
};

const calculateConcentration = (changedInput: string) => {
    const smmUnit = model.value.smmUnit;
    const mcUnit = model.value.mcUnit;
    const smmValue = model.value.smmValue
        ? Number(model.value.smmValue)
        : undefined;
    const mcValue = model.value.mcValue
        ? Number(model.value.mcValue)
        : undefined;
    const mplValue = model.value.mplValue
        ? Number(model.value.mplValue)
        : undefined;

    if (smmValue && mcValue && mplValue) {
        switch (changedInput) {
            case "mpl":
            case "smm":
                model.value.mcValue = _calcMc(
                    smmValue,
                    smmUnit,
                    mplValue,
                    mcUnit,
                );
                break;
            case "mc":
                model.value.mplValue = _calcMplCons(
                    smmValue,
                    smmUnit,
                    mcValue,
                    mcUnit,
                );
            default:
                break;
        }
        return;
    }

    if (mplValue && smmValue) {
        model.value.mcValue = _calcMc(smmValue, smmUnit, mplValue, mcUnit);
        return;
    }
    if (smmValue && mcValue) {
        model.value.mplValue = _calcMplCons(smmValue, smmUnit, mcValue, mcUnit);
        return;
    }

    if (mplValue && mcValue) {
        model.value.smmValue = _calcSmm(mplValue, smmUnit, mcValue, mcUnit);
        return;
    }
};

const _calcMplCons = (
    smmValue: number,
    smmUnit: IUnit,
    mcValue: number,
    mcUnit: IUnit,
) => {
    return cleanNr(
        Number((smmValue / smmUnit.faktor) * (mcValue / mcUnit.faktor) * 1000),
    );
};

const _calcMc = (
    smmValue: number,
    smmUnit: IUnit,
    mplValue: number,
    mcUnit: IUnit,
) => {
    return cleanNr(
        Number(
            (mplValue / ((smmValue / smmUnit.faktor) * 1000)) * mcUnit.faktor,
        ),
    );
};

const _calcSmm = (
    mplValue: number,
    smmUnit: IUnit,
    mcValue: number,
    mcUnit: IUnit,
) => {
    return cleanNr(
        Number((mplValue / (mcValue / mcUnit.faktor) / 1000) * smmUnit.faktor),
    );
};

const _calcSd = (mpl: number, ppm: number, sdUnit: IUnit) => {
    return cleanNr(Number(((1000 * mpl) / ppm) * sdUnit.faktor));
};

const _calcMpl = (sd: number, sdUnit: IUnit, ppm: number) => {
    return cleanNr(Number((ppm * (sd / sdUnit.faktor)) / 1000));
};

const _calcPpm = (mpl: number, sd: number, sdUnit: IUnit) => {
    return cleanNr(Number((1000 * mpl) / (sd / sdUnit.faktor)));
};

const cleanNr = (nr: number) => {
    if (nr >= 1) {
        return Math.floor(10000 * nr) / 10000;
    } else {
        const offset = Math.ceil(Math.log10(nr) * -1);
        return Number(nr.toFixed(offset + 3));
    }
};

const reset = () => {
    model.value = {
        sdUnit: unitCategories.soluteDensity[0],
        smmUnit: unitCategories.soluteMolarMass[0],
        mcUnit: unitCategories.molarConcentration[0],
        mcValue: undefined,
        smmValue: undefined,
        sdValue: undefined,
        ppmValue: undefined,
        mplValue: undefined,
    };
};
</script>

<template>
    <div
        tool-ppmtomgl
        class="tool-content-inner grid grid-cols-3 gap-x-4 gap-y-6"
    >
        <!-- Input unit -->
        <div class="mt-10 col-span-3">
            <div class="units-converter-input-value">
                <label class="text-f7">{{
                    t("Parts per million (by mass)")
                }}</label>
                <input
                    class="input-text-input !px-5 !py-3 w-full !bg-grey-50 rounded-s"
                    type="text"
                    autocomplete="off"
                    v-model="model.ppmValue"
                    @keyup="calculate('ppm')"
                />
            </div>
        </div>

        <!-- Input unit -->
        <div class="units-converter-input-value col-span-2">
            <label class="text-f7">{{ t("Solute density") }}</label>
            <input
                class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                type="text"
                autocomplete="off"
                v-model="model.sdValue"
                @keyup="calculate('sd')"
            />
        </div>

        <div class="units-converter-input-unit col-span-1">
            <label class="text-f7">{{ t("Unit") }}</label>
            <div
                class="input-text-w-btn input-dropdown rounded-s bg-grey-50 mt-2"
                v-dropdown
            >
                <div class="input-text rounded-l-s">
                    <input
                        class="!px-5 !py-4 !h-12 w-full bg-grey-50 rounded-s input-text-input"
                        type="text"
                        autocomplete="off"
                        :value="model.sdUnit.unit"
                        disabled
                    />
                </div>
                <div
                    class="input-text-btn !h-12 relative rounded-r-s bg-grey-50"
                >
                    <i
                        class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2"
                    ></i>
                </div>
                <div class="input-dropdown-elements bgcolor-white w-full">
                    <div class="input-dropdown-elements-inner">
                        <ul>
                            <li
                                class="text-small bold"
                                v-for="unit in unitCategories.soluteDensity"
                                @click="onSelectSdUnit(unit)"
                            >
                                {{ unit.unit }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-span-3">
            <div class="units-converter-input-value">
                <label class="text-f7">{{ t("Milligrams per liter") }}</label>

                <input
                    class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                    type="text"
                    autocomplete="off"
                    v-model="model.mplValue"
                    @keyup="
                        calculateConcentration('mpl');
                        calculate('mpl');
                    "
                />
            </div>
        </div>

        <div class="units-converter-input-value col-span-2">
            <label class="text-f7">{{ t("Solute molar mass") }}</label>
            <input
                class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                type="text"
                autocomplete="off"
                v-model="model.smmValue"
                @keyup="calculateConcentration('smm')"
            />
        </div>

        <div class="units-converter-input-unit col-span-1">
            <label class="text-f7">{{ t("Unit") }}</label>
            <div
                class="input-text-w-btn input-dropdown rounded-s bg-grey-50 mt-2"
                v-dropdown
            >
                <div class="input-text rounded-l-s">
                    <input
                        class="!px-5 !py-4 !h-12 w-full bg-grey-50 rounded-s input-text-input"
                        type="text"
                        autocomplete="off"
                        :value="model.smmUnit.unit"
                        disabled
                    />
                </div>
                <div
                    class="input-text-btn !h-12 relative rounded-r-s bg-grey-50"
                >
                    <i
                        class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2"
                    ></i>
                </div>
                <div
                    class="input-dropdown-elements input-dropdown-elements-up bgcolor-white w-full"
                >
                    <div class="input-dropdown-elements-inner">
                        <ul>
                            <li
                                class="text-small bold"
                                v-for="unit in unitCategories.soluteMolarMass"
                                @click="onSelectSmmUnit(unit)"
                            >
                                {{ unit.unit }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div class="units-converter-input-value col-span-2">
            <label class="text-f7">{{ t("Molar concentration") }}</label>
            <input
                class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-s mt-2"
                type="text"
                autocomplete="off"
                v-model="model.mcValue"
                @keyup="calculateConcentration('mc')"
            />
        </div>

        <div class="units-converter-input-unit col-span-1">
            <label class="text-f7">{{ t("Unit") }}</label>
            <div
                class="input-text-w-btn input-dropdown rounded-s bg-grey-50 mt-2"
                v-dropdown
            >
                <div class="input-text rounded-l-s">
                    <input
                        class="!px-5 !py-4 !h-12 w-full bg-grey-50 rounded-s input-text-input"
                        type="text"
                        autocomplete="off"
                        :value="model.mcUnit.unit"
                        disabled
                    />
                </div>
                <div
                    class="input-text-btn !h-12 relative rounded-r-s bg-grey-50"
                >
                    <i
                        class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2"
                    ></i>
                </div>
                <div
                    class="input-dropdown-elements input-dropdown-elements-up bgcolor-white w-full"
                >
                    <div class="input-dropdown-elements-inner">
                        <ul>
                            <li
                                class="text-small bold"
                                v-for="unit in unitCategories.molarConcentration"
                                @click="onSelectMcUnit(unit)"
                            >
                                {{ unit.unit }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-10">
            <div class="btn-reset btn-large" @click="reset()"></div>
        </div>
    </div>
</template>
